import Analytics from '@/utils/analytics';
import GridIcon from '@assets/icons/grid.svg';
import ListIcon from '@assets/icons/list.svg';
import React, { useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ListDisplayMode } from '@/enums';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useHover } from 'react-native-web-hooks';
import { useUserSettingsContext } from '@/contexts/user-context';

interface Props {
  style?: StyleProp<ViewStyle>;
  type?: 'dark' | 'light';
}

export const ListViewToggle: React.FC<Props> = ({ style, type = 'light' }) => {
  const [styles, theme] = useStyles();
  const { largeItemListDisplayMode, setLargeItemListDisplayMode } =
    useUserSettingsContext();

  const gridButtonRef =
    useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const listButtonRef =
    useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const isGridButtonHovered = useHover(gridButtonRef);
  const isListButtonHovered = useHover(listButtonRef);

  const toggleDisplayOrder = [
    {
      icon: ListIcon,
      isHovered: isListButtonHovered,
      mode: ListDisplayMode.List,
      ref: listButtonRef,
    },
    {
      icon: GridIcon,
      isHovered: isGridButtonHovered,
      mode: ListDisplayMode.Grid,
      ref: gridButtonRef,
    },
  ];

  return (
    <View style={[styles.base, style]}>
      {toggleDisplayOrder.map((option) => {
        const { isHovered, icon: ViewIcon, mode, ref } = option;
        const isSelected = largeItemListDisplayMode === mode;

        const renderDarkBackground = isSelected && type === 'dark';
        const renderLightBackground =
          type === 'light' ? isSelected || isHovered : isSelected;

        return (
          <TouchableOpacity
            key={mode}
            onPress={() => {
              Analytics.track('list_view_toggle.press', {
                listDisplayMode: mode,
              });
              setLargeItemListDisplayMode(mode);
            }}
            ref={ref}
            style={[
              styles.baseButton,
              isHovered || isSelected ? styles.active : null,
              renderDarkBackground
                ? styles.darkBackground
                : renderLightBackground
                  ? styles.lightBackground
                  : null,
            ]}
            testID={`list-view-toggle-${mode}`}
          >
            <ViewIcon
              stroke={
                isSelected
                  ? type === 'light'
                    ? theme.colors.primary
                    : theme.colors.white
                  : theme.colors.inactiveFill
              }
            />
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  active: {
    borderRadius: 4,
    marginHorizontal: 4,
  },
  base: {
    alignItems: 'center',
    borderLeftColor: theme.colors.stroke,
    borderLeftWidth: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  baseButton: {
    marginHorizontal: 4,
  },
  darkBackground: {
    backgroundColor: theme.colors.primary,
  },
  lightBackground: {
    backgroundColor: theme.colors.background,
  },
}));
